import { tv } from "@ui/lib/tv";
import { type ReactNode, useEffect, useId, useState } from "react";

export interface SearchSectionStackItem {
  /** Stable identity used for the expanded-state key and aria wiring. */
  id: string;
  /** Card heading, always visible (e.g. "Where to?", "Start date"). */
  heading: string;
  /** Collapsed-state value preview shown beside the heading (e.g. the chosen place). */
  summary?: ReactNode;
  /** Section body, revealed when the card is expanded. */
  children: ReactNode;
}

export interface SearchSectionStackProps {
  sections: SearchSectionStackItem[];
  /** The single expanded section id, or `null` when all are collapsed. */
  expandedId: string | null;
  onExpandedChange: (id: string | null) => void;
  className?: string;
}

const stack = tv({
  slots: {
    root: "flex flex-col gap-3",
    // Layered drop shadow from the Figma "Drop / Where" search card.
    card: "overflow-hidden rounded-3xl bg-white shadow-md",
    header:
      "flex w-full cursor-pointer items-center justify-between gap-3 px-6 py-5 text-left outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-inset",
    heading: "text-2xl font-extrabold tracking-2xl leading-8 text-neutral-900",
    summary: "min-w-0 truncate text-base font-medium text-neutral-600",
    // The grid-rows 0fr→1fr trick animates height without measuring the body.
    region: "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
    regionInner: "min-h-0 overflow-hidden",
    body: "px-5 pb-5",
  },
});

/**
 * Mobile stacked search experience: a single-open accordion of rounded section
 * cards (Where / Start date / End date / Who) that animate open and closed.
 * Bodies stay mounted so the height transition has content to expand into; the
 * collapsed region is marked `aria-hidden` to keep it out of the a11y tree.
 */
export function SearchSectionStack({ sections, expandedId, onExpandedChange, className }: SearchSectionStackProps) {
  const styles = stack();
  const baseId = useId();
  // Mount a section's body only once it has been opened, then keep it mounted so
  // the collapse transition still has content. This defers each section's
  // contents (e.g. the lazy date pickers) until the user actually opens it,
  // instead of mounting every panel on first render.
  const [openedIds, setOpenedIds] = useState<Set<string>>(() => new Set(expandedId ? [expandedId] : []));
  useEffect(() => {
    if (expandedId && !openedIds.has(expandedId)) {
      setOpenedIds((prev) => new Set(prev).add(expandedId));
    }
  }, [expandedId, openedIds]);

  return (
    <div className={styles.root({ className })}>
      {sections.map((section) => {
        const isOpen = section.id === expandedId;
        const headerId = `${baseId}-${section.id}-header`;
        const regionId = `${baseId}-${section.id}-region`;
        return (
          <div className={styles.card()} key={section.id}>
            <button
              aria-controls={regionId}
              aria-expanded={isOpen}
              className={styles.header()}
              id={headerId}
              onClick={() => onExpandedChange(isOpen ? null : section.id)}
              type="button"
            >
              <span className={styles.heading()}>{section.heading}</span>
              {!isOpen && section.summary ? <span className={styles.summary()}>{section.summary}</span> : null}
            </button>
            <section
              aria-hidden={!isOpen}
              aria-labelledby={headerId}
              className={`${styles.region()} ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
              id={regionId}
            >
              <div className={styles.regionInner()}>
                <div className={styles.body()}>{openedIds.has(section.id) ? section.children : null}</div>
              </div>
            </section>
          </div>
        );
      })}
    </div>
  );
}
